import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { customerService } from '../../services/customerService';
import { supabase } from '../../lib/supabase';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';
import SearchIntegration from '../../components/ui/SearchIntegration';
import Slideshow from '../../components/ui/Slideshow';

import DishCard from './components/DishCard';
import OrderHistoryCard from './components/OrderHistoryCard';
import FavoriteSupplierCard from './components/FavoriteSupplierCard';
import ActiveOrderTracker from './components/ActiveOrderTracker';
import QuickFilters from './components/QuickFilters';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [locationRadius, setLocationRadius] = useState(10);
  const [viewMode, setViewMode] = useState('recommended');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [customerProfile, setCustomerProfile] = useState(null);
  const [recommendedDishes, setRecommendedDishes] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [favoriteSuppliers, setFavoriteSuppliers] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    loadDashboardData();

    // Only set up real-time subscription if user is logged in
    if (user) {
      const setupRealtimeSubscription = async () => {
        try {
          // Get customer profile to filter orders
          const { data: customer } = await customerService?.getCustomerByUserId(user?.id);
          if (!customer) return;

          // Subscribe to order changes for this customer
          const channel = supabase?.channel('dashboard_orders_changes')?.on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'orders',
                filter: `customer_id=eq.${customer?.id}`
              },
              (payload) => {
                console.log('Dashboard order update received:', payload);
                if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
                  loadDashboardData();
                }
              }
            )?.subscribe();

          return () => {
            supabase?.removeChannel(channel);
          };
        } catch (err) {
          console.error('Failed to setup real-time subscription:', err);
        }
      };

      const unsubscribe = setupRealtimeSubscription();

      return () => {
        if (unsubscribe) {
          unsubscribe?.then((cleanup) => cleanup && cleanup());
        }
      };
    }
    // No redirect for unauthenticated users
  }, [user]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification?.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Only fetch customer-specific data if user is logged in
      let customer = null;
      let orders = [];
      let notifs = [];
      if (user) {
        const { data: customerData, error: customerError } = await customerService?.getCustomerByUserId(user?.id);
        if (customerError) throw customerError;
        customer = customerData;
        setCustomerProfile(customer);

        // Get orders
        const { data: ordersData } = await customerService?.getOrders(customer?.id);
        orders = ordersData || [];
        setOrderHistory(orders);

        // Find active order
        const active = orders?.find(o => ['pending', 'preparing', 'ready']?.includes(o?.status));
        setActiveOrder(active || null);

        // Get notifications
        const { data: notifsData } = await customerService?.getNotifications(user?.id);
        notifs = notifsData || [];
        setNotifications(notifs);
      } else {
        setCustomerProfile(null);
        setOrderHistory([]);
        setActiveOrder(null);
        setNotifications([]);
      }

      // Get recommended dishes (always fetch)
      await loadRecommendedDishes();

      // Get favorite suppliers (always fetch)
      const { data: suppliers } = await customerService?.getFavoriteSuppliers();
      setFavoriteSuppliers(suppliers || []);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadRecommendedDishes = async () => {
    try {
      const { data } = await customerService?.getRecommendedDishes();
      // Normalize price field for UI: prefer `price_per_plate` then `price` and expose as `pricePerPlate`
      const normalized = (data || []).map(d => ({
        ...d,
        pricePerPlate: Number(d?.price_per_plate ?? d?.price ?? d?.pricePerPlate ?? 0).toFixed(2),
      }));
      setRecommendedDishes(normalized);
    } catch (err) {
      console.error('Failed to load recommended dishes:', err);
    }
  };

  const searchDishes = async () => {
    try {
      const { data } = await customerService?.searchDishes('', selectedCuisines, selectedDietary);
      setRecommendedDishes(data || []);
    } catch (err) {
      console.error('Failed to search dishes:', err);
    }
  };

  const handleCuisineToggle = (cuisine) => {
    setSelectedCuisines((prev) =>
      prev?.includes(cuisine)
        ? prev?.filter((c) => c !== cuisine)
        : [...prev, cuisine]
    );
  };

  const handleDietaryToggle = (dietary) => {
    setSelectedDietary((prev) =>
      prev?.includes(dietary)
        ? prev?.filter((d) => d !== dietary)
        : [...prev, dietary]
    );
  };

  const mockPopularCuisines = [
    { name: 'Indian', emoji: '🍛' },
    { name: 'Thai', emoji: '🍜' },
    { name: 'Italian', emoji: '🍝' },
    { name: 'Mexican', emoji: '🌮' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={loadDashboardData}>Retry</Button>
        </div>
      </div>
    );
  }

  const handleSearch = (query, filters) => {
    console.log('Search:', query, filters);
  };

  const handleFilterChange = (filters) => {
    console.log('Filters changed:', filters);
  };

  const handleViewDishDetails = (dish) => {
    navigate('/supplier-profile', { state: { dishId: dish?.id } });
  };

  const handleAddToCart = (dish) => {
    navigate('/order-placement', { state: { dish } });
  };

  const handleToggleFavorite = (dishId) => {
    console.log('Toggle favorite:', dishId);
  };

  const handleReorder = (order) => {
    navigate('/order-placement', { state: { reorder: order } });
  };

  const handleViewOrderDetails = (order) => {
    console.log('View order details:', order);
  };

  const handleTrackOrder = (order) => {
    console.log('Track order:', order);
  };

  const handleViewSupplierProfile = (supplier) => {
    navigate('/supplier-profile', { state: { supplierId: supplier?.id } });
  };

  const handleRemoveFavorite = (supplierId) => {
    console.log('Remove favorite supplier:', supplierId);
  };

  const handleContactSupplier = (order) => {
    console.log('Contact supplier:', order);
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Mark as read:', notificationId);
  };

  const handleMarkAllAsRead = () => {
    console.log('Mark all as read');
  };

  const handleOrderUpdate = (updatedOrder) => {
    // Update active order if it matches
    if (activeOrder?.id === updatedOrder?.id) {
      setActiveOrder({ ...activeOrder, ...updatedOrder });
    }
    
    // Update order history
    setOrderHistory((prevOrders) =>
      prevOrders?.map((order) =>
        order?.id === updatedOrder?.id ? { ...order, ...updatedOrder } : order
      )
    );
  };

  const filteredDishes = recommendedDishes?.filter((dish) => {
    const cuisineMatch = selectedCuisines?.length === 0 || selectedCuisines?.includes(dish?.cuisine?.toLowerCase());
    const dietaryMatch = selectedDietary?.length === 0 || selectedDietary?.some((d) => dish?.dietary?.includes(d?.charAt(0)?.toUpperCase() + d?.slice(1)));
    return cuisineMatch && dietaryMatch;
  });

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/*<div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2">
            Welcome back! 👋
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            Discover delicious home-cooked meals from talented chefs near you
          </p>
        </div> */}

        <div className="mb-6">
          {/* Slideshow of recommended dish images */}
          <Slideshow images={recommendedDishes?.map(d => d?.image)?.filter(Boolean)} />
          <SearchIntegration
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            recentSearches={[]}
            popularCuisines={mockPopularCuisines}
            searchResults={[]} />

        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            <QuickFilters
              selectedCuisines={selectedCuisines}
              onCuisineToggle={handleCuisineToggle}
              selectedDietary={selectedDietary}
              onDietaryToggle={handleDietaryToggle}
              locationRadius={locationRadius}
              onRadiusChange={setLocationRadius} />


            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                {viewMode === 'recommended' ? 'Recommended for You' : 'All Dishes'}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('recommended')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                  viewMode === 'recommended' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`
                  }>

                  Recommended
                </button>
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                  viewMode === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`
                  }>

                  All Dishes
                </button>
              </div>
            </div>

            {filteredDishes?.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {filteredDishes?.map((dish) =>
              <DishCard
                key={dish?.id}
                dish={dish}
                onViewDetails={handleViewDishDetails}
                onAddToCart={handleAddToCart}
                onToggleFavorite={handleToggleFavorite} />

              )}
              </div> :

            <div className="bg-card rounded-xl shadow-warm p-8 md:p-12 text-center">
                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={40} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-2">
                  No dishes found
                </h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters to see more options
                </p>
                <Button
                variant="outline"
                onClick={() => {
                  setSelectedCuisines([]);
                  setSelectedDietary([]);
                }}>

                  Clear Filters
                </Button>
              </div>
            }
          </div>

          <div className="space-y-6">
            <ActiveOrderTracker
              order={activeOrder}
              customerProfile={customerProfile}
              onTrackOrder={handleTrackOrder}
              onContactSupplier={handleContactSupplier}
              onOrderUpdate={handleOrderUpdate} />


            <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-lg md:text-xl">
                  Recent Orders
                </h3>
                <button className="text-sm text-primary hover:underline transition-smooth">
                  View all
                </button>
              </div>
              <div className="space-y-3">
                {orderHistory?.slice(0, 2)?.map((order) =>
                <OrderHistoryCard
                  key={order?.id}
                  order={order}
                  onReorder={handleReorder}
                  onViewDetails={handleViewOrderDetails}
                  onTrackOrder={handleTrackOrder} />

                )}
              </div>
            </div>

            <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-lg md:text-xl">
                  Favorite Kitchens
                </h3>
                <button className="text-sm text-primary hover:underline transition-smooth">
                  View all
                </button>
              </div>
              <div className="space-y-3">
                {favoriteSuppliers?.map((supplier) =>
                <FavoriteSupplierCard
                  key={supplier?.id}
                  supplier={supplier}
                  onViewProfile={handleViewSupplierProfile}
                  onRemoveFavorite={handleRemoveFavorite} />

                )}
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Gift" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">
                Special Offer!
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get 20% off on your first custom cake order
              </p>
              <Button variant="default" size="sm" fullWidth>
                Order Custom Cake
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default CustomerDashboard;